/**
 * "Their socials": the handles we know, how big their audience is, and (on
 * click only, since it costs a live lookup) their follower count and latest posts.
 */
import { useEffect, useState } from "react";
import { AtSign, ExternalLink, Eye, Plus, Trash } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button, Input, Select, useToast } from "@/components/ui";
import { Section, dayLabel, num } from "@/features/command/ui";
import { InlineEditField } from "@/features/contacts/InlineEdit";
import { PLATFORMS, platformLabel, profileUrl } from "./shared";
import type { Handle, PublicProfile } from "./types";

const LOOKUP_PLATFORMS = new Set(["instagram", "ig", "tiktok", "youtube", "facebook", "twitter", "x", "linkedin", "threads", "reddit", "bluesky"]);

/** Posts carry a date string or a unix time (seconds or ms). */
function postDate(at: string | number | null): string {
  if (at == null || at === "") return "Recent";
  const n = Number(at);
  const d = Number.isFinite(n) && String(at).trim() !== "" ? new Date(n < 1e12 ? n * 1000 : n) : new Date(String(at));
  return Number.isNaN(d.getTime()) ? "Recent" : dayLabel(d.toISOString());
}

const clean = (list: Handle[]) =>
  list.map((h) => ({ platform: h.platform || null, handle: h.handle.trim().replace(/^@/, ""), ...(h.url ? { url: h.url } : {}) }))
    .filter((h) => h.handle);

export function SocialsCard({
  affiliateId, handles, audience, onSave, onAudience,
}: {
  affiliateId: string;
  handles: Handle[];
  audience: number | null;
  onSave: (patch: { handles?: Handle[]; audience?: number | null }) => Promise<void>;
  onAudience: (n: number) => void;
}) {
  const toast = useToast();
  const [rows, setRows] = useState<Handle[]>(handles);
  const [saving, setSaving] = useState(false);
  const [looking, setLooking] = useState(false);
  const [profile, setProfile] = useState<PublicProfile | null>(null);

  useEffect(() => { setRows(handles); }, [handles]);

  const dirty = JSON.stringify(clean(rows)) !== JSON.stringify(clean(handles));
  const canLookUp = handles.some((h) => h.handle && h.platform && LOOKUP_PLATFORMS.has(String(h.platform).toLowerCase()));

  const save = async () => {
    setSaving(true);
    try { await onSave({ handles: clean(rows) }); } catch { /* the page shows the error */ } finally { setSaving(false); }
  };

  const lookUp = async () => {
    setLooking(true);
    try {
      const d = await api.post<{ profile: PublicProfile }>(`/affiliates/${affiliateId}/profile`, {});
      setProfile(d.profile);
      if (d.profile.followers) onAudience(Number(d.profile.followers));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't read that profile");
    } finally { setLooking(false); }
  };

  return (
    <Section title="Their socials" icon={AtSign}>
      <div className="space-y-3">
        {rows.length === 0 && <p className="text-sm text-zinc-500">No socials yet. Add where they post so you can keep up with them.</p>}
        {rows.map((h, i) => {
          const url = h.handle ? profileUrl(h) : null;
          return (
            <div key={i} className="flex items-center gap-1.5">
              <Select
                value={String(h.platform ?? "other").toLowerCase()}
                onChange={(e) => setRows((r) => r.map((x, j) => (j === i ? { ...x, platform: e.target.value } : x)))}
                className="h-8 w-[7.5rem] shrink-0 text-[13px]"
                aria-label="Platform"
              >
                {PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                {h.platform && !PLATFORMS.some((p) => p.value === String(h.platform).toLowerCase()) && (
                  <option value={String(h.platform).toLowerCase()}>{platformLabel(h.platform)}</option>
                )}
              </Select>
              <div className="min-w-0 flex-1">
                <Input
                  value={h.handle}
                  onChange={(e) => setRows((r) => r.map((x, j) => (j === i ? { ...x, handle: e.target.value, url: null } : x)))}
                  placeholder="@handle"
                  className="h-8 text-[13px]"
                />
              </div>
              {url ? (
                <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200" title="Open their profile">
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ) : <span className="w-8 shrink-0" />}
              <button
                type="button"
                onClick={() => setRows((r) => r.filter((_, j) => j !== i))}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-800/60 hover:text-rose-300"
                title="Remove"
                aria-label="Remove"
              >
                <Trash className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="ghost" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setRows((r) => [...r, { platform: "instagram", handle: "" }])}>
            Add a handle
          </Button>
          {dirty && <Button size="sm" loading={saving} onClick={() => void save()}>Save socials</Button>}
        </div>

        <div className="border-t border-zinc-800/60 pt-3">
          <div className="text-[11px] text-zinc-500">Audience size</div>
          <InlineEditField
            value={audience != null ? String(audience) : ""}
            kind="number"
            label="Audience size"
            placeholder="How many followers?"
            formatDisplay={(v) => `${num(Number(v))} followers`}
            onSave={async (v) => { await onSave({ audience: v == null ? null : Math.max(0, Math.round(Number(v))) }); }}
          />
        </div>

        <div className="border-t border-zinc-800/60 pt-3">
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Eye className="h-3.5 w-3.5" />}
            loading={looking}
            disabled={!canLookUp || dirty}
            onClick={() => void lookUp()}
          >
            See their latest posts
          </Button>
          <p className="mt-1.5 text-[11px] leading-snug text-zinc-500">
            {canLookUp
              ? dirty ? "Save their socials first." : "Looks them up live, which costs a little each time, so it only runs when you click."
              : "Add their Instagram, TikTok, YouTube or similar first."}
          </p>

          {profile && (
            <div className="mt-3 space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-zinc-100">{platformLabel(profile.platform)} @{profile.handle}</span>
                <span className="text-sm tabular-nums text-zinc-300">{profile.followers != null ? `${num(profile.followers)} followers` : "Followers unknown"}</span>
              </div>
              {profile.posts.length === 0 ? (
                <p className="text-xs text-zinc-500">No recent posts came back.</p>
              ) : (
                <ul className="space-y-1.5">
                  {profile.posts.map((post, i) => (
                    <li key={i}>
                      <a
                        href={post.url ?? undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn("block rounded-lg border border-zinc-800/60 px-3 py-2 text-[13px] text-zinc-300", post.url && "hover:border-indigo-400/40 hover:text-zinc-100")}
                      >
                        <span className="line-clamp-2">{post.text || "(no caption)"}</span>
                        <span className="mt-0.5 flex items-center gap-1 text-[11px] text-zinc-500">
                          {postDate(post.at)}
                          {post.url && <ExternalLink className="h-3 w-3" />}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}
